import type { ScheduleTRPCInput } from '@risksmart-app/trpc/src/types';
import type {
  InsertChildDocumentInput,
  InsertDocumentMutation,
} from '@risksmart-app/web-graphql-client/generated/graphql';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTRPC } from 'src/utils/trpc';

type InsertDocumentTRPCInput = Omit<InsertChildDocumentInput, 'schedule'> &
  ScheduleTRPCInput;

/**
 * Maps TRPC mutation response to match the GraphQL mutation structure
 */
const mapTrpcResponseToGraphQL = (trpcData: {
  Id: string;
}): InsertDocumentMutation => {
  return {
    insertChildDocument: {
      __typename: 'IdOutput',
      Id: trpcData.Id,
    },
  };
};

export const useInsertDocumentTRPC = () => {
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  const mutation = useMutation({
    ...trpc.frontend.document.insert.mutationOptions({
      onSuccess: async () => {
        await queryClient.invalidateQueries({
          queryKey: trpc.frontend.document.register.queryKey(),
        });
        await queryClient.invalidateQueries({
          queryKey: trpc.frontend.document.documentById.queryKey(),
        });
        await queryClient.invalidateQueries({
          queryKey: trpc.frontend.document.list.queryKey(),
        });
      },
    }),
  });

  return {
    insertDocument: async (
      variables: InsertDocumentTRPCInput
    ): Promise<InsertDocumentMutation> => {
      const result = await mutation.mutateAsync(variables);

      return mapTrpcResponseToGraphQL(result);
    },
    loading: mutation.isPending,
    error: mutation.error,
  };
};
